import styled from 'styled-components'
import {
  financeControlSkin,
  FONT_STACK,
  sureco,
} from '../../shared/finance.styles'

/* Same look as the billing profile upload: SureCo tokens and control skin. */
export const ReviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${sureco.fg1};
  font-family: ${FONT_STACK};

  ${financeControlSkin}
`

export const ReviewMeta = styled.div`
  font-size: 13px;
  color: ${sureco.fg3};

  strong {
    margin-right: 8px;
    font-weight: 600;
    color: ${sureco.fg1};
  }
`

export const ReviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 14px;
  border: 1px solid ${sureco.border};
  border-radius: 10px;
  background: ${sureco.offWhite};
`

export const ToolbarField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

export const ToolbarFieldEnd = styled(ToolbarField)`
  margin-left: auto;
`

export const ToolbarLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${sureco.fg2};
  white-space: nowrap;
`

/* Mirrors the billing profile table card. */
export const ReviewTableCard = styled.div`
  background: ${sureco.white};
  border-radius: 12px;
  border: 1px solid ${sureco.border};
  box-shadow: ${sureco.shadowSm};
  overflow: hidden;

  &&& .ant-table-thead > tr > th {
    background: ${sureco.offWhite};
    color: ${sureco.fg1};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    height: 48px;
    border-bottom: 1px solid ${sureco.border};
    padding: 0 8px 0 12px;
  }

  .ant-table-thead > tr > th:not(:last-child)::before {
    background-color: ${sureco.borderStrong} !important;
    width: 2px !important;
    height: 14px !important;
  }

  &&& .ant-table-tbody > tr > td {
    font-size: 14px;
    line-height: 18px;
    color: ${sureco.fg1};
    /* Top-aligned so every first line sits level, even when the company cell grows. */
    vertical-align: top;
    border-bottom: 1px solid ${sureco.border};
    padding: 8px 12px;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${sureco.offWhite};
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  && .ant-pagination {
    margin: 0;
    padding: 12px 16px;
    border-top: 1px solid ${sureco.border};
  }

  && .ant-pagination-total-text {
    margin-right: auto;
    font-size: 13px;
    color: ${sureco.fg3};
  }
`

export const StackedCell = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const FileLine = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  color: ${sureco.fg3};

  > span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const CompanyName = styled.span`
  padding-top: 7px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const RowWarning = styled.span`
  font-size: 12px;
  color: ${sureco.noticeText};
`

export const RowActions = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-top: 2px;
`

export const RowIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${sureco.fg3};
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    color: ${sureco.fg1};
    background: ${sureco.offWhite};
  }

  &:focus-visible {
    outline: 2px solid ${sureco.focusRing};
    outline-offset: 1px;
  }
`

export const RemoveRowButton = styled(RowIconButton)`
  &:hover {
    color: ${sureco.danger};
    background: ${sureco.errorLight};
  }
`

export const FooterHint = styled.span`
  margin-right: auto;
  align-self: center;
  font-size: 13px;
  color: ${sureco.noticeText};
`

export const ReviewFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid ${sureco.border};
`

/*
 * One cell's lines. An OE row draws the same lines in every column (inputs
 * 32px, text 22px) so part 2 and the total sit level across the row.
 */
export const CellLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

export const InputLine = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  min-width: 0;
`

export const TextLine = styled.div<{ $tone?: 'warning' }>`
  display: flex;
  align-items: center;
  min-height: 22px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ $tone }) =>
    $tone === 'warning' ? sureco.noticeText : sureco.fg3};
`

export const LineLabel = styled.span`
  font-size: 13px;
  color: ${sureco.fg3};
`

export const LineValue = styled.span`
  font-weight: 600;
  color: inherit;
  font-variant-numeric: tabular-nums;
`
